import { cn, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import {
  DegradationDriver,
  DegradationDriverType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartShape, PieChartConfig } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { useDashboardOrganizationSummary } from '@/hooks/useDashboardOrganizationSummary';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';

const getColorForDriver = (driver: string): string => {
  return (
    colorPalette[`deforestation-degradation-drivers-${driver}`] ?? colorPalette['neutral-black-12'] ?? 'transparent'
  );
};

type ChartItem = {
  name: string;
  value: number;
  driver: string;
  color: string;
};

type DegradationDriversDistributionConfigurationProps = {
  facts: Partial<PlotReportFactElement>[];
  styles?: Record<string, string>;
};

export const useDegradationDriversDistribution = (props?: DegradationDriversDistributionConfigurationProps) => {
  let facts = useOrganizationIndicatorReportingFact(
    [IndicatorName.deforestation_driver_percentage],
    null,
    undefined,
    null,
    true,
  );

  facts =
    facts?.filter((fact) =>
      fact.labels?.some(
        (label) =>
          label.name === PlotReportFactLabelNameEnum.deforestation_driver_type &&
          label.value === DegradationDriverType.dominant,
      ),
    ) ?? [];

  return useDegradationDriversDistributionConfiguration({ ...props, facts });
};

const useDegradationDriversDistributionConfiguration = (props?: DegradationDriversDistributionConfigurationProps) => {
  const { t } = useTranslation();

  const { facts } = props ?? {};

  const { total_assets: totalAssets } = useDashboardOrganizationSummary().data ?? {};

  const noChartData = isNullish(facts) || facts.length === 0 || totalAssets === 0;

  const chartConfig: PieChartConfig[] = [
    {
      shape: ChartShape.pie,
      id: 'degradation_drivers_distribution',
      key: 'degradation_drivers_distribution',
      dataKey: 'value',
      innerRadius: 80,
      outerRadius: 108,
      startAngle: 90,
      endAngle: 450,
      paddingAngle: 0,
      cx: '50%',
      cy: '50%',
      height: 200,
    },
  ];

  const dataWithColors: ChartItem[] =
    facts
      ?.map((item: Partial<PlotReportFactElement>) => {
        const driver =
          (item.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.deforestation_driver)
            ?.value as string) ?? DegradationDriver.no_data;
        const probabilityPct = (((item.value as number) ?? 0) / totalAssets) * 100;
        return {
          name: t(
            `shared.ncaDetail.details.deforestation.degradationDrivers.drivers.${driver as keyof typeof DegradationDriver}`,
          ),
          value: probabilityPct,
          driver,
          color: getColorForDriver(driver),
        };
      })
      .sort((a: ChartItem, b: ChartItem) => a.value - b.value) ?? [];

  const legendItems = dataWithColors.sort((a: ChartItem, b: ChartItem) => b.value - a.value) ?? [];

  const chartWidget = {
    name: 'degradation_drivers_distribution_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      'data-testid': 'degradation-drivers-distribution-chart',
      data: dataWithColors,
      chartConfig,
      legendItems: [],
      tooltip: CustomTooltip,
    },
  };

  const statsWidget = {
    name: 'degradation_drivers_distribution_stats',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: 'flex flex-col w-full px-0 py-0 gap-1',
    },
    widgets: legendItems.map((driver) => ({
      name: 'driver',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: {
        className: 'flex flex-row justify-between px-0 gap-4 w-full',
      },
      widgets: [
        {
          name: 'driver_label',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: {
            className: 'flex flex-row justify-start px-0 gap-2 w-full',
          },
          widgets: [
            {
              name: 'driver_color',
              type: WidgetType.a_icon,
              visible: true,
              relatedData: {
                icon: 'ri-circle-fill',
                style: { color: driver.color },
              },
            },
            {
              name: 'driver_name',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: driver.name,
                className: 'typography-body1 text-text-primary whitespace-pre',
              },
            },
          ],
        },
        {
          name: 'driver_probability',
          type: WidgetType.a_text,
          visible: true,
          relatedData: {
            text: `${getDisplayNumber(driver.value, window.navigator.language)}%`,
            className: 'typography-caption text-text-secondary whitespace-pre',
          },
        },
      ],
    })),
  };

  const graphWidget = {
    name: 'degradation_drivers_distribution_content',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: 'flex flex-row items-start justify-start w-full gap-4',
    },
    widgets: [chartWidget, statsWidget],
  };

  return {
    name: 'deforestation_degradation_drivers_distribution_card',
    type: WidgetType.o_card,
    visible: !noChartData,
    relatedData: {
      header: t('shared.ncaDetail.details.deforestation.degradationDrivers.labels.heading'),
      styles: {
        root: cn('h-full rounded-2xl w-full', props?.styles?.root ?? ''),
        header: cn(
          'typography-overline pt-4 pl-4 justify-center items-center text-text-secondary border-none',
          props?.styles?.header ?? '',
        ),
        body: cn('h-full py-0 pt-3 flex flex-col justify-center gap-4 border-none', props?.styles?.body ?? ''),
      },
    },
    widgets: [graphWidget],
  };
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload: ChartItem;
  }>;
};

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className='rounded-md bg-white-100 px-3 py-2 shadow-lg border border-neutral-black-12'>
      <Stack direction='row' centerMain spacing={2} className='mb-1'>
        <div className='w-3 h-3 rounded-full' style={{ backgroundColor: getColorForDriver(data.driver) }} />
        <span className='font-medium text-sm text-gray-900'>
          {t(
            `shared.ncaDetail.details.deforestation.degradationDrivers.drivers.${data.driver as keyof typeof DegradationDriver}`,
          )}
        </span>
      </Stack>
      <div className='text-sm text-gray-600'>{getDisplayNumber(data.value, window.navigator.language)}%</div>
    </div>
  );
};
